import { FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  LinkBox,
} from '@chakra-ui/react';
import { useDebounce } from 'usehooks-ts';
import { ReactFlowProvider } from 'reactflow';

import { ReactComponent as DotsIcon } from '../../../assets/icons/dots.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import TrashIcon from '../../../assets/icons/red-trash.svg';
import WorkflowIcon from '../../../assets/projects/workflow.svg';
import FormIcon from '../../../assets/projects/form.svg';
import EditIcon from '../../../assets/icons/edit.svg';
import EyeIcon from '../../../assets/icons/eye.svg';
import DuplicateIcon from '../../../assets/icons/duplicate.svg';
import {
  useDeleteWorkflowMutation,
  useGetAllWorkflowsQuery,
} from '../../../services';
import {
  FilterDrawer,
  CenteredSpinner,
  RoundedBadge,
  Paginator,
  TableHeader,
  DeleteModal,
  UserData,
} from '../../../components';

import { formatDate } from '../../../utils';
import { NewWorkflowModal, WorkflowBuilder } from '.';
import { WorkflowBuilderMode } from '../../../models';

export const WorkflowsList: FC<{ phaseId: string }> = ({ phaseId }) => {
  const [formeId, setFormeId] = useState<number>();
  const [clonedWorkflowId, setClonedWorkflowId] = useState<number>();
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<number>();
  const [workflowBuilderMode, setWorkflowBuilderMode] =
    useState<WorkflowBuilderMode>();
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, isLoading, isFetching } = useGetAllWorkflowsQuery(
    {
      page,
      phaseId,
      workflowName: debouncedSearchTerm,
    },
    {
      refetchOnReconnect: true,
    }
  );

  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isNewWorkflowModalOpen,
    onOpen: onOpenNewWorkflowModal,
    onClose: onCloseNewWorkflowModal,
  } = useDisclosure();

  const {
    isOpen: isWorkflowBuilderOpen,
    onOpen: onOpenWorkflowBuilderOpen,
    onClose: onCloseWorkflowBuilderOpen,
  } = useDisclosure();

  const {
    isOpen: isDeleteWorkflowModalOpen,
    onOpen: onOpenWorkflowModalOpen,
    onClose: onCloseDeleteWorkflowModal,
  } = useDisclosure();

  const [deleteWorkflow] = useDeleteWorkflowMutation();

  return (
    <Box h="full">
      {isOpen && (
        <FilterDrawer isOpen={isOpen} onClose={onClose} filters={[]} />
      )}

      {isNewWorkflowModalOpen && (
        <NewWorkflowModal
          isOpen={isNewWorkflowModalOpen}
          onClose={(formId, workflowId) => {
            setFormeId(formId);
            setClonedWorkflowId(workflowId);
            setWorkflowBuilderMode(WorkflowBuilderMode.Create);

            onCloseNewWorkflowModal();
            onOpenWorkflowBuilderOpen();
          }}
        />
      )}

      {isWorkflowBuilderOpen && formeId && (
        <ReactFlowProvider>
          <WorkflowBuilder
            isOpen={isWorkflowBuilderOpen}
            onClose={onCloseWorkflowBuilderOpen}
            formId={formeId}
            workflowId={clonedWorkflowId}
            phaseId={Number(phaseId)}
            mode={workflowBuilderMode}
          />
        </ReactFlowProvider>
      )}

      {isDeleteWorkflowModalOpen && (
        <DeleteModal
          isOpen={isDeleteWorkflowModalOpen}
          onClose={isDeleted => {
            if (isDeleted && selectedWorkflowId)
              void deleteWorkflow(selectedWorkflowId);

            onCloseDeleteWorkflowModal();
          }}
        />
      )}

      <Box display="flex" flexDirection="column" flex="1" h="full">
        <HStack justify="space-between" mt="4" align="start">
          <HStack>
            <Heading color="custom_blue.1" fontSize="2xl">
              Workflows
            </Heading>
          </HStack>

          <HStack>
            <Button
              onClick={onOpenNewWorkflowModal}
              bg="custom_orange.2"
              _hover={{ bg: 'custom_orange.2' }}
              color="white"
              fontWeight="semibold"
              fontSize="sm"
              leftIcon={<PlusIcon />}
            >
              Create Workflows
            </Button>
          </HStack>
        </HStack>

        <VStack align="stretch" bg="white" my="4" gap="3">
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Button
              leftIcon={<FiltersIcon />}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
              _hover={{ bg: 'none' }}
              onClick={onOpen}
            >
              Filters
            </Button>
          </HStack>

          {isLoading || isFetching ? (
            <CenteredSpinner />
          ) : (
            <Box>
              <TableContainer
                h="full"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                minH="70vh"
              >
                <Table variant="simple">
                  <Thead bg="custom_gray.2" px="3">
                    <Tr>
                      <TableHeader title="Workflow Name" />
                      <TableHeader title="Form Name" />
                      <TableHeader title="Status" />
                      <TableHeader title="Last Updated At" />
                      <TableHeader title="Updated By" />
                      <TableHeader title="Actions" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data?.data.content.map(
                      ({
                        id,
                        status,
                        formName,
                        formId,
                        name,
                        modifiedAt,
                        modifiedBy,
                      }) => (
                        <LinkBox key={id} as={Tr}>
                          <Td border="none">
                            <HStack>
                              <HStack w="10" h="10">
                                <Image src={WorkflowIcon} />
                              </HStack>
                              <VStack align="flex-start">
                                <Text
                                  color="custom_blue.1"
                                  fontWeight="semibold"
                                  fontSize="sm"
                                >
                                  {name}
                                </Text>
                              </VStack>
                            </HStack>
                          </Td>

                          <Td border="none">
                            <HStack>
                              <HStack w="10" h="10">
                                <Image src={FormIcon} />
                              </HStack>
                              <VStack align="flex-start">
                                <Text
                                  color="custom_blue.1"
                                  fontWeight="semibold"
                                  fontSize="sm"
                                >
                                  {formName}
                                </Text>
                              </VStack>
                            </HStack>
                          </Td>

                          <Td border="none">
                            <RoundedBadge colorSchema={status}>
                              {status}
                            </RoundedBadge>
                          </Td>

                          <Td border="none">{formatDate(modifiedAt)}</Td>

                          <Td border="none">
                            {modifiedBy ? (
                              <UserData
                                roleName={modifiedBy.role.name}
                                firstName={modifiedBy.firstName}
                                lastName={modifiedBy.lastName}
                                pictureUrl={
                                  modifiedBy.profilePictureThumbnailURL
                                }
                              />
                            ) : (
                              '-'
                            )}
                          </Td>

                          <Td color="custom_blue.1" fontSize="xs" border="none">
                            <Menu>
                              <MenuButton
                                as={IconButton}
                                icon={<DotsIcon />}
                                aria-label="Actions of documents"
                                bg="transparent"
                              />
                              <MenuList p="0">
                                <MenuItem
                                  py="3"
                                  border="1px"
                                  borderColor="blackAlpha.50"
                                  onClick={() => {
                                    setWorkflowBuilderMode(
                                      WorkflowBuilderMode.View
                                    );
                                    setClonedWorkflowId(id);
                                    setFormeId(formId);
                                    onOpenWorkflowBuilderOpen();
                                  }}
                                  _hover={{
                                    bg: 'custom_orange.4',
                                    color: 'custom_orange.2',
                                  }}
                                >
                                  <HStack>
                                    <Image
                                      src={EyeIcon}
                                      alt="Archive document"
                                    />
                                    <Text fontSize="sm" fontWeight="medium">
                                      view
                                    </Text>
                                  </HStack>
                                </MenuItem>

                                <MenuItem
                                  py="3"
                                  border="1px"
                                  borderColor="blackAlpha.50"
                                  _hover={{
                                    bg: 'custom_orange.4',
                                    color: 'custom_orange.2',
                                  }}
                                  onClick={() => {
                                    setWorkflowBuilderMode(
                                      WorkflowBuilderMode.Edit
                                    );
                                    setClonedWorkflowId(id);
                                    setFormeId(formId);
                                    onOpenWorkflowBuilderOpen();
                                  }}
                                >
                                  <HStack>
                                    <Image src={EditIcon} alt="Edit document" />
                                    <Text fontSize="sm" fontWeight="medium">
                                      Edit
                                    </Text>
                                  </HStack>
                                </MenuItem>
                                {/* TODO: reuse after implementation */}
                                {/* <MenuItem
                                  py="3"
                                  border="1px"
                                  borderColor="blackAlpha.50"
                                  _hover={{
                                    bg: 'custom_orange.4',
                                    color: 'custom_orange.2',
                                  }}
                                  onClick={() => {}}
                                >
                                  <HStack>
                                    <Image
                                      src={DuplicateIcon}
                                      alt="Duplicate document"
                                    />
                                    <Text fontSize="sm" fontWeight="medium">
                                      duplicate
                                    </Text>
                                  </HStack>
                                </MenuItem> */}
                                <MenuItem
                                  py="3"
                                  border="1px"
                                  borderColor="blackAlpha.50"
                                  _hover={{
                                    bg: 'custom_orange.4',
                                    color: 'custom_orange.2',
                                  }}
                                  onClick={() => {
                                    setSelectedWorkflowId(id);
                                    onOpenWorkflowModalOpen();
                                  }}
                                >
                                  <HStack>
                                    <Image src={TrashIcon} alt="Delete phase" />
                                    <Text fontSize="sm" fontWeight="medium">
                                      Delete
                                    </Text>
                                  </HStack>
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </Td>
                        </LinkBox>
                      )
                    )}
                  </Tbody>
                </Table>

                <Paginator
                  pageCount={Number(data?.data.totalPages)}
                  onPageChange={page => setPage(page)}
                />
              </TableContainer>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
